import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bookingsService } from '../services/bookings.service'
import { Booking, BookingStatus } from '../types'
import toast from 'react-hot-toast'

export function useBookings(params?: {
  caseId?: string
  status?: BookingStatus
  page?: number
  limit?: number
}) {
  return useQuery({
    queryKey: ['bookings', params],
    queryFn: () => bookingsService.getBookings(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useBooking(id: string) {
  return useQuery({
    queryKey: ['booking', id],
    queryFn: () => bookingsService.getBookingById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useBookingStats() {
  return useQuery({
    queryKey: ['bookings', 'stats'],
    queryFn: () => bookingsService.getBookingStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCreateBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bookingData: any) => bookingsService.createBooking(bookingData),
    onSuccess: (newBooking) => {
      toast.success('Booking created successfully')
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      queryClient.setQueryData(['booking', newBooking.id], newBooking)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create booking'
      toast.error(message)
    },
  })
}

export function useUpdateBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      bookingsService.updateBooking(id, data),
    onSuccess: (updatedBooking) => {
      toast.success('Booking updated successfully')
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.setQueryData(['booking', updatedBooking.id], updatedBooking)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update booking'
      toast.error(message)
    },
  })
}

export function useConfirmBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => bookingsService.confirmBooking(id),
    onSuccess: (updatedBooking) => {
      toast.success('Booking confirmed successfully')
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.setQueryData(['booking', updatedBooking.id], updatedBooking)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to confirm booking'
      toast.error(message)
    },
  })
}

export function useStartBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => bookingsService.startBooking(id),
    onSuccess: (updatedBooking) => {
      toast.success('Booking started successfully')
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.setQueryData(['booking', updatedBooking.id], updatedBooking)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to start booking'
      toast.error(message)
    },
  })
}

export function useCompleteBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => bookingsService.completeBooking(id),
    onSuccess: (updatedBooking) => {
      toast.success('Booking completed successfully')
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.setQueryData(['booking', updatedBooking.id], updatedBooking)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to complete booking'
      toast.error(message)
    },
  })
}

export function useCancelBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      bookingsService.cancelBooking(id, reason),
    onSuccess: (updatedBooking) => {
      toast.success('Booking cancelled successfully')
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.setQueryData(['booking', updatedBooking.id], updatedBooking)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to cancel booking'
      toast.error(message)
    },
  })
}

export function useProviderBookings() {
  return useMutation({
    mutationFn: (params: {
      providerId: string
      status?: BookingStatus
      date?: string
    }) => bookingsService.getProviderBookings(params.providerId, params),
  })
}